<template>
  <div>
    <div class="basic-loading-bounce-container" v-if="isLoadingCard">
      <LoadingBounce></LoadingBounce>
    </div>

    <section class="card-cover" v-else>
      <card-cover-image
        v-if="coverImage"
        :image-url="coverImage"
      />

      <h2 class="card-title">
        {{ title }}
      </h2>

      <div class="card-cover__subtitle">
        {{ gradeName }} {{ subjectName }}
      </div>

      <div class="row">
        <template v-if="chapterName">
          <div class="col-2 card-cover__chapter-label">{{ $t('viewer.card.chapter.label') }}</div>
          <div class="col-10 card-cover__chapter-value">{{ chapterName }}</div>
        </template>
        <template v-if="domainName">
          <div class="col-2 card-cover__domain-label">{{ $t('viewer.card.domain.label') }}</div>
          <div class="col-10 card-cover__domain-value">{{ domainName }}</div>
        </template>
      </div>

      <div class="row card-cover__teacher">
        <div class="col-2"><img class="card-cover__teacher-image" :src="teacherProfileImage" alt="teacher" /></div>
        <div class="col-10">
          <div class="card-cover__teacher-name">{{ teacherName }}</div>
          <div class="card-cover__teacher-class">
            {{ `${schoolName} ${classGrade !== null ? $t('common.grade_label', { grade: classGrade }) : ''} ${className}` }}
          </div>
        </div>
      </div>

      <card-additional-info
        :quizzes-count="quizCount"
        :videos-count="videoCount">
      </card-additional-info>

      <template v-if="goal">
        <div class="card-cover__subtitle">{{ $t('viewer.card.goal.label') }}</div>
        <card-goal :goal="goal"></card-goal>
      </template>

      <div class="card-cover__date">
        <strong v-if="assignmentDday">D-{{ assignmentDday }}&nbsp;&nbsp;</strong>
        {{ assignmentStartDate }} - {{ $t('comp.card.due_date', { date: dueDate }) }}
      </div>
      <div class="card-cover__date" v-if="isSubmission">
        {{ $t('comp.card.submission_date', { date: submissionDate }) }}
      </div>

      <div class="card-actions">
        <button
          class="btn btn-rectangle-primary w-full"
          v-if="isAssignmentSubmitted"
          v-on:click="goNextPage()">
          {{ $t('btn.see_results') }}
        </button>
        <button
          class="btn btn-rectangle-primary w-full"
          v-if="!isAssignmentSubmitted && !isSubmission && !isClassStudent"
          v-on:click="openAssignmentsOnlyStudentsModal()">
          {{ $t('viewer.page.assignments.button') }}
        </button>
        <router-link
          class="btn btn-rectangle-primary w-full"
          v-if="!isAssignmentSubmitted && !isSubmission && isClassStudent"
          :to="{ name: 'pages.show', params: { cardId: card.id, page: 1 } }">
          {{ $t('viewer.page.assignments.button') }}
        </router-link>
      </div>

      <b-modal id="assignments-only-students-modal"
               class="basic-modal hide-cancel-btn"
               v-on:ok="goNextPage()"
               v-bind:ok-title="$t('common.close')">
        <div slot="modal-header">
          <h5 class="typography-heandline-5">
            {{ $t('common.modal.assignments_only_students.title') }}
          </h5>
        </div>

        <div class="typography-caption typography-color-gray mb-4 mt-6">
          {{ $t('common.modal.assignments_only_students.desc') }}
        </div>
      </b-modal>
      <b-modal id="assignments-init-modal"
               class="basic-modal"
               v-on:ok="goNextPage()"
               v-bind:close-title="$t('common.close')"
               v-bind:ok-title="$t('common.confirm')">
        <div slot="modal-header">
          <h5 class="typography-headline-5">
            {{ $t('common.modal.assignments_init.title') }}
          </h5>
        </div>

        <div class="typography-caption typography-color-gray mb-4 mt-6">
          {{ $t('common.modal.assignments_init.desc') }}
        </div>
      </b-modal>
      <b-modal id="assignments-end-modal"
               class="basic-modal hide-cancel-btn"
               v-on:ok="goNextPage()"
               v-bind:ok-title="$t('common.close')">
        <div slot="modal-header">
          <h5 class="typography-headline-5">
            {{ $t('common.modal.assignments_end.title') }}
          </h5>
        </div>

        <div class="typography-caption typography-color-gray mb-4 mt-6">
          {{ $t('common.modal.assignments_end.desc', { date: dueDate }) }}
        </div>
      </b-modal>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isNil, find } from 'lodash';
import Mobile from '@/mixins/Mobile';
import labels from '@/mixins/labels';

import datetime from '@/filters/datetime';
import CardCoverImage from '../components/CardCoverImage';
import CardInfo from '../components/CardInfo';
import CardAdditionalInfo from '../components/CardAdditionalInfo';
import CardGoal from '../components/CardGoal';

export default {
  name: 'card',

  mixins: [
    labels,
    Mobile,
  ],

  components: {
    CardCoverImage,
    CardInfo,
    CardAdditionalInfo,
    CardGoal,
  },

  data() {
    return {
      isLoadingCard: false,
    };
  },

  computed: {
    ...mapGetters({
      card: 'getCard',
      cardId: 'getCardId',
      isEnd: 'getIsEnd',
      currentUser: 'currentUser',
      currentUserId: 'currentUserId',
      isClassStudent: 'isClassStudent',
      isAssignmentSubmitted: 'isAssignmentSubmitted',
      gradeName: 'getGradeName',
      subjectName: 'getSubjectName',
      teacherProfileImage: 'getTeacherProfileImage',
      teacherName: 'getTeacherName',
      className: 'getClassName',
      schoolName: 'getSchoolName',
      chapterName: 'getChapterName',
      domainName: 'getDomainName',
      coreConcept: 'getCoreConcept',
      classGrade: 'getClassGrade',
      isStudentUser: 'isStudentUser',
    }),

    assignedCard() {
      return this.card && this.card.assigned_card;
    },

    classId() {
      return this.card && this.card.class_id;
    },

    coverImage() {
      return this.assignedCard && this.assignedCard.cover_image;
    },

    title() {
      return this.assignedCard && this.assignedCard.title;
    },

    quizCount() {
      return this.assignedCard && this.assignedCard.quiz_count;
    },

    videoCount() {
      return this.assignedCard && this.assignedCard.video_count;
    },

    goal() {
      return this.assignedCard && this.assignedCard.goal;
    },

    submissions() {
      return this.card.submissions &&
          this.card.submissions.filter(submission =>
            submission.student_id === this.currentUserId);
    },

    submission() {
      return this.submissions && this.submissions[0];
    },

    isSubmission() {
      return this.submission && this.submission.is_submitted;
    },

    dueDate() {
      return datetime(this.card.due_date);
    },

    submissionDate() {
      return datetime(this.submission.submitted_at || this.submission.updated_at);
    },

    assignmentStartDate() {
      return datetime(this.card.start_date);
    },

    assignmentDday() {
      if (new Date(this.card.due_date).getTime() < new Date().getTime()) {
        return false;
      }

      const oneDay = (1000 * 60 * 60 * 24);
      const differenceDay = new Date(this.card.due_date).getTime() - new Date().getTime();
      return Math.round(differenceDay / oneDay);
    },

  },

  methods: {
    ...mapActions({
      createAssignmentSubmission: 'createAssignmentSubmission',
      fetchClassStudent: 'fetchClassStudent',
      setIsClassStudent: 'setIsClassStudent',
      syncClassStudents: 'syncClassStudents',
      fetchCard: 'fetchCard',
      fetchCoreConcept: 'fetchCoreConcept',
    }),

    openAssignmentsInitModal() {
      this.$root.$emit('bv::show::modal', 'assignments-init-modal');
    },

    openAssignmentsEndModal() {
      this.$root.$emit('bv::show::modal', 'assignments-end-modal');
    },

    openAssignmentsOnlyStudentsModal() {
      this.$root.$emit('bv::show::modal', 'assignments-only-students-modal');
    },

    goNextPage() {
      this.$router.push(`/viewer/cards/${this.cardId}/pages/1`);
    },
  },

  beforeMount() {
    this.isLoadingCard = true;
    if (this.isClasstingAndroid) {
      this.callActionbar(true);
    }

    this.fetchCard(this.cardId)
      .then((card) => {
        if (isNil(card.assigned_card.core_concept_id)) {
          return true;
        }

        return this.fetchCoreConcept(card.assigned_card.core_concept_id);
      })
      .then(() => (
        this.fetchClassStudent({ classId: this.classId, studentId: this.currentUserId })
      ))
      .then((classStudent) => {
        const isClassStudent = !isNil(classStudent);
        this.setIsClassStudent(isClassStudent);
        return true;
      })
      .then(() => {
        if (!isNil(this.submission)) {
          return true;
        }

        if (!this.isStudentUser) {
          return true;
        }

        return this.syncClassStudents(this.card.class_id)
          .then(students => (
            find(students, student => (student.student_id === this.currentUserId))
          ))
          .then((student) => {
            if (isNil(student)) {
              return this.setIsClassStudent(false);
            }

            if (!this.isEnd) {
              return this.createAssignmentSubmission(this.cardId)
                .then(() => (
                  this.setIsClassStudent(true)
                ));
            }

            return true;
          });
      })
      .then(() => {
        if (this.isAssignmentSubmitted || this.isEnd) {
          return this.$router.replace({ name: 'cards.results', params: { cardId: this.cardId } });
        }

        this.isLoadingCard = false;
        return true;
      })
      .catch(() => this.$root.$emit('bv::show::modal', 'basic-error-alert'));
  },
};
</script>

<style lang="scss" scoped>
  @import '@/assets/styles/components/_breakpoints';
  @import '@/assets/styles/components/_typographies';

  .card-title {
    font-weight: 300;
    line-height: 1.33;
    color: #ffffff;
    font-size: 34px;

    @media (max-width: $tablet-breakpoint - 1) {
      font-size: 24px;
    }
  }

  .card-cover__subtitle {
      font-size: 14px;
      color: #ffffff;
      padding-top: 8px;
      padding-bottom: 8px;
      font-weight: 400;
  }

  .card-cover__teacher {
    padding-top: 24px;
    padding-bottom: 24px;
    color: #ffffff;

    .card-cover__teacher-name {
      font-size: 14px;
      font-weight: 400;

      @media (min-width: $tablet-breakpoint) {
        font-size: 20px;
      }
    }

    .card-cover__teacher-image {
      border: solid 1px #c3c3c3;
      border-radius: 38px;
      @media (max-width: $tablet-breakpoint - 1) {
        width: 38px;
        height: 38px;
      }

      @media (min-width: $tablet-breakpoint) {
        width: 44px;
        height: 44px;
      }
    }

    .card-cover__teacher-class {
      font-size: 14px;
      font-weight: 200;

      @media (min-width: $tablet-breakpoint) {
        font-size: 20px;
      }
    }

  }

  .card-cover__chapter-label,
  .card-cover__domain-label {
    font-size: 14px;
    color: #ffffff;
    font-weight: 400;

    @media (min-width: $tablet-breakpoint) {
      font-size: 20px;
    }
  }

  .card-cover__chapter-value,
  .card-cover__domain-value {
    font-size: 14px;
    font-weight: 300;

    @media (min-width: $tablet-breakpoint) {
      font-size: 20px;
    }
  }

  .card-cover {
    display: block;
    max-width: 570px;

    margin: 0 auto;
    padding: percentage(60px/360px) 22px;

    color: #fff;
  }

  .cover-image {
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    margin: 0;
    padding: 0;

    z-index: -1;
  }

  .cover-image img {
    width: 110%;
    height: 110%;

    margin: -20px;

    object-fit: cover;
    filter: blur(12px);
  }

  .card-actions {

    @media (min-width: $tablet-breakpoint) {
      padding: 64px 0;
    }

    @media (max-width: $tablet-breakpoint - 1) {
      position: fixed;
      right: 0;
      left: 0;
      bottom: 0;
    }

    a, button {
      padding: 16px;

      &:hover,
      &:focus {
        color:#ffffff;
        background-color: #3DE4AD;
      }

      &:active {
        color:#ffffff;
        background-color: #00B68F
      }
    }
  }

  .card-cover__date {
    font-size: 14px;
    font-weight: 200;
    color: #ffffff;

    @media (min-width: $tablet-breakpoint) {
      font-size: 20px;
    }
  }
</style>
